import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Link2 } from 'lucide-react';

import { useReferralCodeDetail } from '@/lib/api/referralHooks';
import { Stat, Td, Th, fmtDate, referralLink, usd } from './referralShared';

/** Per-code drill-in: referrer info + a per-referred-customer breakdown.
 *  Linked from the conversion-tracking table on `/admin/referrals`. */
export function AdminReferralCodeScreen() {
  const { code = '' } = useParams();
  const { data, isLoading, isError } = useReferralCodeDetail(code || null);
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex flex-col gap-16">
      <div>
        <Link
          to="/admin/referrals"
          className="inline-flex items-center gap-6 type-label-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          All referral codes
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-10">
        <h1 className="type-heading-xl font-mono">{code}</h1>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(referralLink(code));
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            } catch {
              /* clipboard unavailable — the link is visible in the table */
            }
          }}
          className="inline-flex items-center gap-4 px-8 py-4 rounded border border-line text-ink-muted hover:bg-sunken hover:text-ink type-label-sm"
        >
          <Link2 size={12} strokeWidth={1.5} />
          {copied ? 'Copied' : 'Copy share link'}
        </button>
      </div>

      {isLoading && (
        <div className="p-16 text-center text-ink-muted type-label-md">
          Loading…
        </div>
      )}
      {isError && (
        <div className="p-16 text-center text-ink-muted">
          Failed to load — this code may have been deleted.
        </div>
      )}

      {data && (
        <div className="border border-line rounded-lg bg-raised overflow-hidden">
          <div className="p-16 border-b border-line flex flex-wrap gap-24 type-label-sm">
            <Stat label="Referrer">
              {data.referrer.name || data.referrer.email || '—'}
            </Stat>
            <Stat label="Referrals sent">{data.referrals_sent}</Stat>
            <Stat label="Orders placed">{data.orders_placed}</Stat>
            <Stat label="Orders shipped">{data.orders_shipped}</Stat>
            <Stat label="Credit earned">{usd(data.credit_earned_cents)}</Stat>
            <Stat label="Code created">{fmtDate(data.created_at)}</Stat>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken border-b border-line text-left text-ink-muted">
                <tr>
                  <Th>Referred customer</Th>
                  <Th>Attributed</Th>
                  <Th align="right">Orders placed</Th>
                  <Th align="right">Orders shipped</Th>
                </tr>
              </thead>
              <tbody>
                {data.referred_customers.map((r) => (
                  <tr key={r.patient_id} className="border-t border-line-subtle">
                    <Td>
                      <div>{r.name || '—'}</div>
                      <div className="text-ink-muted text-11">
                        {r.email || '—'}
                      </div>
                    </Td>
                    <Td>{fmtDate(r.attributed_at)}</Td>
                    <Td align="right">{r.orders_placed}</Td>
                    <Td align="right">{r.orders_shipped}</Td>
                  </tr>
                ))}
                {data.referred_customers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-16 text-center text-ink-muted">
                      No friends have used this code yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
